import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, MessageSquare, Paperclip } from "lucide-react"
import { api, ErrorApi } from "@/api/cliente"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { AvanceNuevo } from "./AvanceNuevo"
import type { Asignable } from "./asignables"
import { ComentarioNuevo } from "./ComentarioNuevo"
import { DependeDe } from "./DependeDe"
import { EditorTarea } from "./EditorTarea"
import { EnlacesTarea } from "./EnlacesTarea"
import { type Evento, LineaDeTiempo } from "./LineaDeTiempo"
import { type Microtarea, Microtareas } from "./Microtareas"
import { type Adjunto, SoportesTarea } from "./SoportesTarea"
import { SubtareasDe } from "./SubtareasDe"
import type { Tarea } from "./TarjetaTarea"

/**
 * Detalle de una tarea: su descripción, a quién está asignada y su conversación.
 *
 * ⚠ **Reasignar y comentar exigen permisos distintos, y aquí se respetan por
 * separado.** El servidor pide `tarea:asignar` para lo primero y `tarea:actualizar`
 * para lo segundo — quitarle una tarea a alguien es de quien coordina; comentarla,
 * de quien la ejecuta. Esconder cada control es cosmético: la decisión la toma
 * `can()` en cada petición.
 *
 * ⚠ **La conversación no se pierde al reasignar.** Los comentarios cuelgan de la
 * tarea, no de la persona: si se pasa a otro, quien la reciba lee por qué está como
 * está. Es la mitad del valor de tener comentarios.
 *
 * ⚠ **La conversación se relee cada 3 s mientras la ficha está abierta.** No son
 * websockets y no se pretende que lo sean: es sondeo corto sobre UNA tarea, la que
 * se está mirando, y se detiene al cerrar la ficha. El tablero sigue a 60 s. Con
 * esto un mensaje aparece antes de que a nadie le dé tiempo a preguntar «¿lo viste?»,
 * y no hay conexión que autenticar, ni canal de Redis entre réplicas, ni reconexión
 * que escribir.
 *
 * ⚠ **El marco es del `Cajon`.** Aquí no hay título ni botón de cerrar: los pone la
 * ficha, que es quien sabe cómo se abre y se cierra. Dos cierres —uno del cajón y
 * otro de dentro— es el camino a que uno de los dos deje de funcionar.
 */

type Comentario = {
  id: number
  tarea_id: number
  autor_id: number
  cuerpo: string
  creado_en: string
  adjuntos: Adjunto[]
}

type Detalle = Tarea & {
  descripcion: string | null
  creado_por: number
  comentarios: Comentario[]
  subtareas: Tarea[]
  /** Los soportes del encargo: los que no cuelgan de ningún comentario. */
  adjuntos: Adjunto[]
  eventos: Evento[]
  microtareas: Microtarea[]
  /** La macro de la que cuelga, entera. Nula cuando la tarea es de primer nivel. */
  padre: Tarea | null
  /** Lo decide el servidor: la pantalla no conoce `creado_por` ni los permisos. */
  puedo_editar_core: boolean
}

type Props = {
  tareaId: number
  asignables: Asignable[]
  nombreDe: (id: number | null) => string | undefined
  /** Saltar a otra tarea sin cerrar la ficha: se usa al pulsar una subtarea. */
  onAbrirOtra: (id: number) => void
}

export function PanelTarea({ tareaId, asignables, nombreDe, onAbrirOtra }: Props) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)

  const consulta = useQuery({
    queryKey: ["tarea", tareaId],
    queryFn: () => api.get<Detalle>(`/api/v1/tareas/${tareaId}`),
    refetchInterval: 3_000,
  })

  /** Tras escribir, la tarjeta del tablero también cambia: se invalidan las dos. */
  const refrescar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["tarea", tareaId] })
    clienteQuery.invalidateQueries({ queryKey: ["tareas"] })
  }

  const asignar = useMutation({
    mutationFn: (asignado_a: number | null) =>
      api.post(`/api/v1/tareas/${tareaId}/asignar`, { asignado_a }),
    onSuccess: refrescar,
  })

  const puedeAsignar = tienePermiso(usuario, "tarea:asignar")
  // El mismo permiso cubre editar los campos y comentar: las dos son formas de
  // actualizar la tarea, y el servidor las gobierna con `tarea:actualizar`.
  const puedeActualizar = tienePermiso(usuario, "tarea:actualizar")

  return (
    <div>
      {consulta.isPending && (
        <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando la tarea…
        </p>
      )}

      {consulta.isError && (
        <p
          role="alert"
          className="flex items-center gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          No se pudo cargar la tarea.
        </p>
      )}

      {consulta.data && (
        <>
          {/* Arriba del todo, antes del título: de qué depende esta tarea es lo
              primero que hay que saber para entender qué es. */}
          {consulta.data.padre !== null && (
            <DependeDe padre={consulta.data.padre} onAbrir={onAbrirOtra} />
          )}

          <p className="tabular text-xs" style={{ color: "var(--fg-muted)" }}>
            {consulta.data.codigo} · creada por {nombreDe(consulta.data.creado_por)}
          </p>
          {/* `key` por identificador: saltar a otra tarea REMONTA el editor con sus
              valores. Sin esto, lo tecleado en la anterior se quedaría en pantalla
              sobre una tarea que no es. */}
          <EditorTarea
            key={consulta.data.id}
            tarea={consulta.data}
            // Las DOS: el servidor dice si este usuario manda sobre el encargo, y
            // `tarea:actualizar` es la puerta del `PATCH`. Quien creó la tarea pero
            // no puede actualizarla vería un formulario que responde 403 al guardar.
            puedeEditarCore={consulta.data.puedo_editar_core && puedeActualizar}
            arrancada={consulta.data.estado !== "PENDIENTE"}
            onGuardado={refrescar}
          />

          <label className="mt-4 block text-sm">
            Asignada a
            <select
              aria-label="Asignada a"
              disabled={!puedeAsignar || asignar.isPending}
              className="campo mt-1 disabled:opacity-60"
              value={consulta.data.asignado_a ?? ""}
              onChange={(e) =>
                asignar.mutate(e.target.value === "" ? null : Number(e.target.value))
              }
            >
              {/* Devolverla a la bandeja sin dueño es una operación legítima, no un
                  hueco: el servidor acepta `null` a propósito. */}
              <option value="">Sin asignar</option>
              {asignables.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </label>

          {asignar.isError && (
            <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
              {asignar.error instanceof ErrorApi
                ? asignar.error.message
                : "No se pudo reasignar la tarea."}
            </p>
          )}

          <SubtareasDe
            macroId={tareaId}
            tipo={consulta.data.tipo}
            subtareas={consulta.data.subtareas}
            puedeCrear={tienePermiso(usuario, "tarea:crear")}
            onAbrir={onAbrirOtra}
            onCreada={refrescar}
          />

          {puedeActualizar && (
            <AvanceNuevo
              tareaId={tareaId}
              pendiente={consulta.data.estado === "PENDIENTE"}
              onRegistrado={refrescar}
            />
          )}

          <Microtareas
            tareaId={tareaId}
            microtareas={consulta.data.microtareas}
            puedeEditar={puedeActualizar}
            onCambio={refrescar}
          />

          <SoportesTarea
            tareaId={tareaId}
            adjuntos={consulta.data.adjuntos}
            puedeAdjuntar={puedeActualizar}
            onSubido={refrescar}
          />

          <h3 className="mt-5 flex items-center gap-2 text-sm font-medium">
            <MessageSquare size={14} aria-hidden={true} />
            Comentarios
            <span className="tabular text-xs" style={{ color: "var(--fg-muted)" }}>
              {consulta.data.comentarios.length}
            </span>
          </h3>

          {consulta.data.comentarios.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
              Todavía no hay comentarios.
            </p>
          ) : (
            <ul className="mt-2 space-y-3">
              {consulta.data.comentarios.map((c) => (
                <li
                  key={c.id}
                  data-testid={`comentario-${c.id}`}
                  className="border-l-2 pl-3"
                  style={{ borderColor: "var(--border)" }}
                >
                  <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {nombreDe(c.autor_id)} ·{" "}
                    <span className="tabular">{comoFechaHora(c.creado_en)}</span>
                  </p>
                  <p className="whitespace-pre-wrap text-sm">{c.cuerpo}</p>
                  {c.adjuntos.length > 0 && (
                    <ul className="mt-1 space-y-1">
                      {c.adjuntos.map((a) => (
                        <li key={a.id}>
                          {/*
                            Enlace a un endpoint autenticado, no a `storage/`, que no
                            se sirve como estático. `Content-Disposition` decide en el
                            servidor si se abre o se descarga: una imagen en línea, lo
                            demás como archivo.
                          */}
                          <a
                            href={`/api/v1/tareas/adjuntos/${a.id}/contenido`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`adjunto-${a.id}`}
                            className="flex items-center gap-1 text-xs underline"
                            style={{ color: "var(--fg-muted)" }}
                          >
                            <Paperclip size={12} aria-hidden={true} />
                            {a.nombre_original}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}

          {puedeActualizar && <ComentarioNuevo tareaId={tareaId} onPublicado={refrescar} />}

          <EnlacesTarea tareaId={tareaId} puedeRevocar={puedeActualizar} />

          <LineaDeTiempo eventos={consulta.data.eventos} nombreDe={nombreDe} />
        </>
      )}
    </div>
  )
}
